export interface BezierControlPoint {
  x: number; // time (0-24 hours)
  y: number; // load (0-100%)
  cp1x: number; // control point 1 x
  cp1y: number; // control point 1 y
  cp2x: number; // control point 2 x
  cp2y: number; // control point 2 y
}

export type BezierCurve = BezierControlPoint[];

/**
 * Evaluates a Bezier curve at a specific time point
 */
export function evaluateBezierCurve(
  sortedPoints: BezierCurve,
  time: number,
  timeScale: number = 24
): number {
  if (sortedPoints.length === 0) return 0;
  
  // Handle edge cases
  if (time <= 0) return sortedPoints[0].y;
  if (time >= timeScale) return sortedPoints[sortedPoints.length - 1].y;
  
  // Find the segment that contains this time
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const point = sortedPoints[i];
    const nextPoint = sortedPoints[i + 1];
    
    if (time >= point.x && time <= nextPoint.x) {
      // Normalize time within this segment (0 to 1)
      const t = (time - point.x) / (nextPoint.x - point.x);
      
      // Bezier curve evaluation using cubic Bezier formula
      // P(t) = (1-t)³P₀ + 3(1-t)²tP₁ + 3(1-t)t²P₂ + t³P₃
      const p0 = point.y;
      const p1 = point.cp2y;
      const p2 = nextPoint.cp1y;
      const p3 = nextPoint.y;
      
      const invT = 1 - t;
      const result = 
        invT * invT * invT * p0 +
        3 * invT * invT * t * p1 +
        3 * invT * t * t * p2 +
        t * t * t * p3;
      
      return Math.max(0, result); // Ensure non-negative load
    }
  }
  
  // Fallback - shouldn't happen if time is within range
  return sortedPoints[0].y;
}

/**
 * Samples the load curve at regular intervals (typically hourly)
 */
export function sampleLoadCurve(
  controlPoints: BezierCurve,
  timeScale: number = 24,
  sampleCount: number = 24
): number[] {
  if (!controlPoints || controlPoints.length === 0) {
    return new Array(sampleCount).fill(0);
  }
  
  const sortedPoints = [...controlPoints].sort((a, b) => a.x - b.x);
  const samples: number[] = [];
  
  for (let i = 0; i < sampleCount; i++) {
    const time = (i / (sampleCount - 1)) * timeScale;
    const load = evaluateBezierCurve(sortedPoints, time, timeScale);
    samples.push(load);
  }
  
  return samples;
}

/**
 * Converts percentage load values to MW based on system base load
 */
export function convertPercentageToMW(
  percentageValues: number[],
  baseLoadMW: number = 100
): number[] {
  return percentageValues.map(percent => (percent / 100) * baseLoadMW);
}